import Layout from "@/components/application/layout";
import ValidationErrors from "@/components/inertia/validation-errors";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSeparator,
  FieldSet,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import useFieldArray from "@/hooks/use-field-array";
import useTranslations from "@/hooks/use-translations";
import { store_path } from "@/lib/routes";
import { AdminPageProps } from "@/types";
import { useForm, usePage } from "@inertiajs/react";
import { XIcon } from "lucide-react";
import { FormEvent, ReactNode } from "react";

type StoreFormData = {
  store: {
    name: string;
    subdomain: string;
    currency: string;
    social_links: { type: string; url: string }[];
    seo_keywords: string;
    seo_description: string;
    og_image: File | string | null;
  };
};

function StoreSettingsPage() {
  const { current_store } = usePage<AdminPageProps>().props;
  const { t } = useTranslations("admin.stores.settings");

  const storeSettingsForm = useForm<StoreFormData>({
    store: {
      name: current_store!.name,
      subdomain: current_store!.subdomain,
      currency: current_store!.currency,
      social_links: current_store!.social_links,
      seo_keywords: current_store!.seo_keywords ?? "",
      seo_description: current_store!.seo_description ?? "",
      og_image: current_store!.og_image_signed_id,
    },
  });

  const { append, remove, update } = useFieldArray(
    storeSettingsForm,
    "store.social_links"
  );

  function onSubmit(event: FormEvent) {
    event.preventDefault();

    storeSettingsForm.patch(store_path(current_store!));
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">{t("title")}</h2>

        <div className="flex gap-3">
          <Button variant="outline" disabled={storeSettingsForm.processing}>
            {t("shared.actions.cancel")}
          </Button>
          <Button
            type="submit"
            form={`edit-store-form-${current_store!.id}`}
            disabled={storeSettingsForm.processing}
          >
            {storeSettingsForm.processing && <Spinner />}
            {t("shared.actions.save")}
          </Button>
        </div>
      </div>

      <form
        id={`edit-store-form-${current_store!.id}`}
        onSubmit={onSubmit}
        className="max-w-3xl flex flex-col gap-5"
      >
        <FieldSet>
          <FieldLegend>{t("general.legend")}</FieldLegend>
          <FieldDescription>{t("general.description")}</FieldDescription>

          <FieldGroup>
            <Field
              orientation="horizontal"
              data-invalid={!!storeSettingsForm.errors["store.name"]}
            >
              <FieldLabel htmlFor="name" className="w-72">
                {t("general.name_label")}
              </FieldLabel>

              <Input
                id="name"
                name="store[name]"
                placeholder="Enter the store name"
                value={storeSettingsForm.data.store.name}
                onChange={(event) =>
                  storeSettingsForm.setData("store.name", event.target.value)
                }
              />

              {storeSettingsForm.errors["store.name"] && (
                <ValidationErrors
                  errors={storeSettingsForm.errors["store.name"]}
                />
              )}
            </Field>

            <Field
              orientation="horizontal"
              data-invalid={!!storeSettingsForm.errors["store.subdomain"]}
            >
              <FieldLabel htmlFor="subdomain" className="w-72">
                {t("general.subdomain_label")}
              </FieldLabel>

              <Input
                id="subdomain"
                name="store[subdomain]"
                placeholder="Enter the store subdomain"
                value={storeSettingsForm.data.store.subdomain}
                onChange={(e) =>
                  storeSettingsForm.setData((values) => ({
                    ...values,
                    subdomain: e.target.value,
                  }))
                }
              />

              {storeSettingsForm.errors["store.subdomain"] && (
                <ValidationErrors
                  errors={storeSettingsForm.errors["store.subdomain"]}
                />
              )}
            </Field>

            <Field orientation="horizontal">
              <FieldLabel className="w-72">{t("general.currency_label")}</FieldLabel>

              <Select
                name="store[currency]"
                value={storeSettingsForm.data.store.currency}
                onValueChange={(value) =>
                  storeSettingsForm.setData((values) => ({
                    ...values,
                    state: value,
                  }))
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t("general.currency_placeholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="usd">USD</SelectItem>
                  <SelectItem value="eur">EUR</SelectItem>
                  <SelectItem value="pen">PEN</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <div className="flex flex-row gap-3 items-start">
              <FieldLabel className="w-72">
                {t("general.social_profiles_label")}
              </FieldLabel>

              <FieldGroup className="gap-4 w-full">
                {storeSettingsForm.data.store.social_links.map(
                  (socialLink, index) => (
                    <Field key={index}>
                      <FieldContent className="flex flex-row gap-4">
                        <Select
                          value={socialLink.type}
                          onValueChange={(value) =>
                            update(index, { type: value })
                          }
                        >
                          <SelectTrigger className="w-48">
                            <SelectValue placeholder={t("general.type_placeholder")} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="twitter">
                              {t("general.type_twitter")}
                            </SelectItem>
                            <SelectItem value="facebook">
                              {t("general.type_facebook")}
                            </SelectItem>
                            <SelectItem value="instagram">
                              {t("general.type_instagram")}
                            </SelectItem>
                            <SelectItem value="other">
                              {t("general.type_other")}
                            </SelectItem>
                          </SelectContent>
                        </Select>

                        <InputGroup>
                          <InputGroupInput
                            type="url"
                            value={socialLink.url}
                            onChange={(event) =>
                              update(index, { url: event.target.value })
                            }
                          />
                          <InputGroupAddon align="inline-end">
                            <InputGroupButton
                              type="button"
                              variant="ghost"
                              size="icon-xs"
                              onClick={() => remove(index)}
                            >
                              <XIcon />
                            </InputGroupButton>
                          </InputGroupAddon>
                        </InputGroup>
                      </FieldContent>
                    </Field>
                  )
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => append({ type: "", url: "" })}
                  // disabled={fields.length >= 5}
                >
                  {t("general.add_social_profile")}
                </Button>
              </FieldGroup>
            </div>
          </FieldGroup>
        </FieldSet>

        <FieldSeparator />

        <FieldSet>
          <FieldLegend>{t("seo.legend")}</FieldLegend>
          <FieldDescription>{t("seo.description")}</FieldDescription>

          <FieldGroup>
            <Field
              orientation="horizontal"
              data-invalid={!!storeSettingsForm.errors["store.seo_keywords"]}
            >
              <FieldLabel htmlFor="seo_keywords" className="w-72">
                {t("seo.keywords_label")}
              </FieldLabel>

              <Input
                id="seo_keywords"
                name="store[seo_keywords]"
                placeholder={t("seo.keywords_placeholder")}
                value={storeSettingsForm.data.store.seo_keywords}
                onChange={(event) =>
                  storeSettingsForm.setData(
                    "store.seo_keywords",
                    event.target.value
                  )
                }
              />

              {storeSettingsForm.errors["store.seo_keywords"] && (
                <ValidationErrors
                  errors={storeSettingsForm.errors["store.seo_keywords"]}
                />
              )}
            </Field>

            <Field
              orientation="horizontal"
              data-invalid={!!storeSettingsForm.errors["store.seo_description"]}
            >
              <FieldLabel htmlFor="seo_description" className="w-72 self-start">
                {t("seo.description_label")}
              </FieldLabel>

              <Textarea
                id="seo_description"
                name="store[seo_description]"
                placeholder={t("seo.description_placeholder")}
                value={storeSettingsForm.data.store.seo_description}
                onChange={(event) =>
                  storeSettingsForm.setData(
                    "store.seo_description",
                    event.target.value
                  )
                }
              />

              {storeSettingsForm.errors["store.seo_description"] && (
                <ValidationErrors
                  errors={storeSettingsForm.errors["store.seo_description"]}
                />
              )}
            </Field>

            <Field orientation="horizontal">
              <FieldLabel htmlFor="og_image" className="w-72">
                {t("seo.og_image_label")}
              </FieldLabel>

              <div className="grid grid-cols-2 gap-4 w-fit">
                {current_store!.og_image_url ? (
                  <img
                    src={current_store!.og_image_url}
                    className="aspect-auto"
                  />
                ) : (
                  <span />
                )}
                <Input
                  id="og_image"
                  name="store[og_image]"
                  type="file"
                  onChange={(event) =>
                    event.target.files &&
                    storeSettingsForm.setData(
                      "store.og_image",
                      event.target.files[0]
                    )
                  }
                />
              </div>
            </Field>
          </FieldGroup>
        </FieldSet>
      </form>
    </div>
  );
}

StoreSettingsPage.layout = (page: ReactNode) => <Layout children={page} />;

export default StoreSettingsPage;
